import {useCallback, useContext, useId, useMemo, useState} from "react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {Box, SegmentedControl, Table, Text} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {GPTMetaContext} from "../MetaProvider";
import {isExternalSourceType, SourceInfo} from "../../../../../types";
import {unitPhrase} from "./unitPhrase";
import {metricLabel} from "./metricLabel";
import {intlLocaleForLanguage} from "./numberLocale";
import classes from "./ChartBlock.module.css";

export const PROFF_TOPIC_ID_PREFIX = "proff:";

/**
 * True when every listed source index resolves to a Proff ExternalSource.
 * One unresolved or non-Proff index drops the caption for the whole block.
 * Shared by ChartBlock and CompareBlock so the caption rule stays one rule.
 */
export const resolvesToProffSources = (
    source: number | number[] | string | string[] | undefined,
    sources: SourceInfo[] | undefined
): boolean => {
    if (source === undefined) {
        return false;
    }
    const refs: (number | string)[] = Array.isArray(source) ? source : [source];
    return refs.every((ref) => {
        // A number is a source index (format 1); a string is a section block id (format 2).
        const found =
            typeof ref === "number"
                ? sources?.find((s) => s.index === ref)
                : sources?.find((s) => s.sections?.some((section) => section.id === ref));
        return (
            !!found &&
            isExternalSourceType(found.type) &&
            !!found.topicId?.startsWith(PROFF_TOPIC_ID_PREFIX)
        );
    });
};

const isStringArray = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((v) => typeof v === "string");

export type ChartPoint = {
    x: number | string;
    y: number;
};

export type ChartSeries = {
    label: string;
    displayLabel?: string;
    points: ChartPoint[];
};

export type ChartKind = "line" | "bar";

export type ChartNode = {
    type: "chart";
    kind: ChartKind;
    // A backend-identified metric resolves to its label via metricLabel and
    // takes priority over title. title stays for backward compatible content.
    metric?: string;
    title: string;
    unit: string;
    series: ChartSeries[];
    source?: number | number[] | string | string[];
    provider?: string;
    displayTitle?: string;
    subtitle?: string;
    note?: string;
    defaultView?: ChartKind | "table";
};

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

const isNonEmptyString = (value: unknown): value is string =>
    typeof value === "string" && value.length > 0;

const isChartPointX = (value: unknown): value is number | string =>
    isFiniteNumber(value) || isNonEmptyString(value);

const isChartPoint = (value: unknown): value is ChartPoint =>
    typeof value === "object" &&
    value !== null &&
    isChartPointX((value as ChartPoint).x) &&
    isFiniteNumber((value as ChartPoint).y);

const isChartSeries = (value: unknown): value is ChartSeries =>
    typeof value === "object" &&
    value !== null &&
    typeof (value as ChartSeries).label === "string" &&
    Array.isArray((value as ChartSeries).points) &&
    (value as ChartSeries).points.length > 0 &&
    (value as ChartSeries).points.every(isChartPoint);

const isNumberArray = (value: unknown): value is number[] =>
    Array.isArray(value) && value.length > 0 && value.every(isFiniteNumber);

export type IsChartNodeOptions = {
    // Set when the fence's own language is "chart": the language already
    // decides this is a chart, so a "type" field is optional and any value
    // in it is ignored. A bare JSON paragraph never sets this.
    ignoreType?: boolean;
};

/**
 * Parses a code block's raw text as a chart node. Returns null for anything
 * that is not valid JSON or does not match the chart shape, including a
 * partially streamed block whose JSON is not complete yet.
 */
export function isChartNode(text: unknown, options: IsChartNodeOptions = {}): ChartNode | null {
    if (typeof text !== "string") {
        return null;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return null;
    }

    if (typeof parsed !== "object" || parsed === null) {
        return null;
    }

    const candidate = parsed as Record<string, unknown>;
    if (!options.ignoreType && candidate.type !== "chart") {
        return null;
    }
    if (candidate.kind !== "line" && candidate.kind !== "bar") {
        return null;
    }
    if (!Array.isArray(candidate.series) || candidate.series.length === 0) {
        return null;
    }
    if (!candidate.series.every(isChartSeries)) {
        return null;
    }

    const xTypes = new Set(
        candidate.series.flatMap((s) => (s as ChartSeries).points.map((p) => typeof p.x))
    );
    if (xTypes.size > 1) {
        return null;
    }

    const source = isFiniteNumber(candidate.source)
        ? candidate.source
        : isNumberArray(candidate.source)
          ? candidate.source
          : typeof candidate.source === "string"
            ? candidate.source
            : isStringArray(candidate.source)
              ? candidate.source
          : undefined;
    const provider = typeof candidate.provider === "string" ? candidate.provider : undefined;
    const metric = typeof candidate.metric === "string" ? candidate.metric : undefined;
    const optionalText = (value: unknown) => typeof value === "string" && value.trim().length ? value : undefined;

    return {
        type: "chart",
        kind: candidate.kind,
        metric,
        title: typeof candidate.title === "string" ? candidate.title : "",
        unit: typeof candidate.unit === "string" ? candidate.unit : "",
        series: (candidate.series as ChartSeries[]).map((series) => ({...series, displayLabel: optionalText(series.displayLabel)})),
        source,
        provider,
        displayTitle: optionalText(candidate.displayTitle),
        subtitle: optionalText(candidate.subtitle),
        note: optionalText(candidate.note),
        defaultView: candidate.defaultView === "line" || candidate.defaultView === "bar" || candidate.defaultView === "table"
            ? candidate.defaultView : undefined,
    };
}

// Fixed categorical order, reusing the app's own theme colors. A hue is
// never assigned by rank, only by this fixed position, so a series keeps
// its color as other series come and go. Exported so CompareBlock draws
// from the same order.
export const SERIES_COLORS = [
    "var(--mantine-color-dibblue-6)",
    "var(--mantine-color-mars-6)",
    "var(--mantine-color-sjøgrønn-7)",
    "var(--mantine-color-blåbær-6)",
    "var(--mantine-color-gneis-6)",
];

export const CHART_UNAVAILABLE_DEFAULT = "Diagrammet kunne ikke vises.";

export const ChartUnavailable = () => {
    const {t} = useTranslation();
    return <Text size="sm" c="dimmed" className={classes.footnote}>{t("chart.unavailable", {defaultValue: CHART_UNAVAILABLE_DEFAULT})}</Text>;
};

type MergedRow = {x: number | string; [series: string]: number | string | undefined};

/** Internal keys keep series data separate even when display labels match. */
const seriesDataKey = (index: number) => `series-${index}`;

/** Merges series into rows keyed by x (year or category label), so charts
 *  with series that cover different x values still line up on one shared
 *  axis. Numeric x (years) sorts ascending; string x (categories) keeps
 *  first-seen order. */
function mergeSeries(series: ChartSeries[]): MergedRow[] {
    const rows = new Map<number | string, MergedRow>();
    series.forEach((s, index) => {
        const dataKey = seriesDataKey(index);
        for (const point of s.points) {
            const row = rows.get(point.x) ?? {x: point.x};
            row[dataKey] = point.y;
            rows.set(point.x, row);
        }
    });
    const merged = Array.from(rows.values());
    const isNumericX = merged.every((row) => typeof row.x === "number");
    return isNumericX
        ? merged.sort((a, b) => (a.x as number) - (b.x as number))
        : merged;
}

type ChartBlockProps = {
    chart: ChartNode;
};

export const ChartBlock = ({chart}: ChartBlockProps) => {
    const {t, i18n} = useTranslation();
    const meta = useContext(GPTMetaContext);
    const gradientId = useId();
    const data = useMemo(() => mergeSeries(chart.series), [chart.series]);
    const identity = JSON.stringify(chart);
    const [selection, setSelection] = useState<{identity: string; view: ChartKind | "table"}>({
        identity,
        view: chart.defaultView === "line" && chart.kind === "bar" && data.some((row) => typeof row.x === "string")
            ? chart.kind
            : chart.defaultView ?? chart.kind,
    });
    const initialView = chart.defaultView === "line" && chart.kind === "bar" && data.some((row) => typeof row.x === "string")
        ? chart.kind : chart.defaultView ?? chart.kind;
    const view = selection.identity === identity ? selection.view : initialView;
    const lineAvailable = chart.kind === "line" || data.every((row) => typeof row.x === "number");
    const showLegend = chart.series.length > 1;
    const isArea = view === "line" && chart.series.length === 1;

    const showProffAttribution = useMemo(
        () => resolvesToProffSources(chart.source, meta?.rawSources ?? meta?.sources),
        [meta?.rawSources, meta?.sources, chart.source]
    );

    const locale = intlLocaleForLanguage(i18n.resolvedLanguage ?? i18n.language);
    const numberFormat = useMemo(
        () => new Intl.NumberFormat(locale, {maximumFractionDigits: 1}),
        [locale]
    );
    const tableNumberFormat = useMemo(
        () => new Intl.NumberFormat(locale, {maximumFractionDigits: 20}),
        [locale]
    );
    const formatValue = useCallback(
        (value: number | string) => (typeof value === "number" ? numberFormat.format(value) : value),
        [numberFormat]
    );
    const formatTooltipValue = useCallback(
        (value: number | string) => (typeof value === "number" ? tableNumberFormat.format(value) : value),
        [tableNumberFormat]
    );

    const title = chart.displayTitle ?? (chart.metric ? metricLabel(chart.metric, t) : chart.title);
    const originalTitle = chart.metric ? metricLabel(chart.metric, t) : chart.title;
    const caption = [title, chart.unit ? unitPhrase(chart.unit, t) : ""]
        .filter(Boolean)
        .join(" ");

    return (
        <Box className={classes.container}>
            {caption && (
                <Text size="sm" c="dimmed" className={classes.title} title={chart.displayTitle && chart.displayTitle !== originalTitle ? originalTitle : undefined}>
                    ({caption})
                </Text>
            )}
            {chart.subtitle && <Text size="sm" c="dimmed">{chart.subtitle}</Text>}
            <Box className={classes.viewControl}>
                <SegmentedControl
                    size="xs"
                    aria-label={t("chart.view.switch", {defaultValue: "Chart view"})}
                    value={view}
                    onChange={(nextView) =>
                        setSelection({identity, view: nextView as ChartKind | "table"})
                    }
                    data={[
                        ...(lineAvailable
                            ? [{value: "line", label: t("chart.view.line", {defaultValue: "Line"})}]
                            : []),
                        {value: "bar", label: t("chart.view.bar", {defaultValue: "Bar"})},
                        {value: "table", label: t("chart.view.table", {defaultValue: "Table"})},
                    ]}
                />
            </Box>
            {view === "table" ? (
                <Box className={classes.tableScroll}>
                    <Table striped withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th scope="col">
                                    {typeof data[0]?.x === "number"
                                        ? t("chart.view.period", {defaultValue: "Period"})
                                        : t("chart.view.category", {defaultValue: "Category"})}
                                </Table.Th>
                                {chart.series.map((series, index) => (
                                    <Table.Th key={seriesDataKey(index)} scope="col" className={classes.numericCell}>
                                        <span title={series.displayLabel && series.displayLabel !== series.label ? series.label : undefined}>{series.displayLabel ?? series.label}</span>
                                    </Table.Th>
                                ))}
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {data.map((row) => (
                                <Table.Tr key={row.x}>
                                    <Table.Th scope="row">{row.x}</Table.Th>
                                    {chart.series.map((_series, index) => {
                                        const dataKey = seriesDataKey(index);
                                        const value = row[dataKey];
                                        return (
                                            <Table.Td key={dataKey} className={classes.numericCell}>
                                                {typeof value === "number"
                                                    ? tableNumberFormat.format(value)
                                                    : "—"}
                                            </Table.Td>
                                        );
                                    })}
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Box>
            ) : (
            <ResponsiveContainer width="100%" height={260}>
                {view === "bar" ? (
                    <BarChart data={data} margin={{top: 8, right: 16, left: 8, bottom: 8}}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="x" tickLine={false} />
                        <YAxis tickFormatter={formatValue} tickLine={false} />
                        <Tooltip formatter={formatTooltipValue} />
                        {showLegend && <Legend />}
                        {chart.series.map((s, index) => (
                            <Bar
                                key={seriesDataKey(index)}
                                dataKey={seriesDataKey(index)}
                                name={s.displayLabel ?? s.label}
                                {...(s.displayLabel && s.displayLabel !== s.label ? {title: s.label} : {})}
                                fill={SERIES_COLORS[index % SERIES_COLORS.length]}
                                radius={[4, 4, 0, 0]}
                            />
                        ))}
                    </BarChart>
                ) : isArea ? (
                    <AreaChart data={data} margin={{top: 8, right: 16, left: 8, bottom: 8}}>
                        <defs>
                            <linearGradient id={`chart-area-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={SERIES_COLORS[0]} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={SERIES_COLORS[0]} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="x" tickLine={false} />
                        <YAxis tickFormatter={formatValue} tickLine={false} />
                        <Tooltip formatter={formatTooltipValue} />
                        <Area
                            type="monotone"
                            dataKey={seriesDataKey(0)}
                            name={chart.series[0].displayLabel ?? chart.series[0].label}
                            stroke={SERIES_COLORS[0]}
                            strokeWidth={2}
                            fill={`url(#chart-area-${gradientId})`}
                            dot={{r: 3}}
                            connectNulls
                        />
                    </AreaChart>
                ) : (
                    <LineChart data={data} margin={{top: 8, right: 16, left: 8, bottom: 8}}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="x" tickLine={false} />
                        <YAxis tickFormatter={formatValue} tickLine={false} />
                        <Tooltip formatter={formatTooltipValue} />
                        {showLegend && <Legend />}
                        {chart.series.map((s, index) => (
                            <Line
                                key={seriesDataKey(index)}
                                type="monotone"
                                dataKey={seriesDataKey(index)}
                                name={s.displayLabel ?? s.label}
                                {...(s.displayLabel && s.displayLabel !== s.label ? {title: s.label} : {})}
                                stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
                                strokeWidth={2}
                                dot={{r: 3}}
                                connectNulls
                            />
                        ))}
                    </LineChart>
                )}
            </ResponsiveContainer>
            )}
            {showProffAttribution && (
                <Text size="xs" c="dimmed" className={classes.footnote}>
                    {t("source.companyLookup")}
                </Text>
            )}
            {chart.note && <Text size="xs" c="dimmed" className={classes.footnote}>{chart.note}</Text>}
        </Box>
    );
};

// Matches the fence language "chart", or a JSON object opening with a "type"
// field whose value is a prefix of "chart" so far (e.g. `{"type":"cha`,
// still streaming in) or reads "chart" in full once closed. A value that
// diverges from "chart", such as "references", never matches.
const TYPE_FIELD_OPEN = /^\{\s*"type"\s*:\s*"/;

export const looksLikeChartFence = (lang: string | null, trimmed: string): boolean => {
    if (lang === "chart") {
        return true;
    }
    const opened = TYPE_FIELD_OPEN.exec(trimmed);
    if (!opened) {
        return false;
    }
    const rest = trimmed.slice(opened[0].length);
    const closingQuote = rest.indexOf('"');
    return closingQuote === -1
        ? "chart".startsWith(rest)
        : rest.slice(0, closingQuote) === "chart";
};

/**
 * Placeholder shown while a chart's JSON is still streaming in. Same height
 * as the rendered chart so the layout does not jump once it arrives.
 */
export const ChartSkeleton = () => {
    const {t} = useTranslation();
    return (
        <Box
            className={classes.skeleton}
            aria-busy="true"
            aria-label={t("chart.loading")}
        />
    );
};
